import { createSlice } from "@reduxjs/toolkit";
import axios from "axios";

export const productManagementSlide = createSlice({
    name: "productManagement",
    initialState: {
        page: 0,
        pageCount: 0,
        listData: null,

        success: false,
        fail: false,
        
        detailData: null,
        responseData: null,
        errorDetailData: null,
        summaryData: null,
        totalRow: 0,

        bundleSourceOptions: [],

        importSuccess: false,
        importFail: false,
        importResponseData: null,
        downloadTemplateSuccess: false,
        downloadTemplateFail: false,
    },
    reducers: {
        doGetProductManagementListSuccess: (state, action) => {
            state.listData = action.payload.data;
            state.page = action.payload?.current_page;
			state.pageCount = action.payload?.page_count;
			state.totalRow = action.payload?.total_row;
        },
        doGetProductManagementListFail: (state, action) => {
            state.listData = null;
            state.page = 0;
			state.pageCount = 0;
			state.totalRow = 0;
        },
        doGetProductManagementSummarySuccess: (state, action) => {
            state.summaryData = action.payload?.data;
        },
        doGetProductManagementSummaryFail: (state, action) => {
            state.summaryData = null;
        },

        doGetProductManagementDetailSuccess: (state, action) => {
            state.detailData = action.payload.data;
        },
        doGetProductManagementDetailFail: (state, action) => {
            state.detailData = null;
        },

        doAddProductManagementSuccess: (state, action) => {
            state.success = true;
            state.fail = false;
			state.responseData = action.payload;
        },
        doAddProductManagementFail: (state, action) => {
            state.success = false;
            state.fail = true;
			state.errorDetailData = action.payload;
        },

        doUpdateProductManagementSuccess: (state, action) => {
            state.success = true;
            state.fail = false;
        },
        doUpdateProductManagementFail: (state, action) => {
            state.success = false;
            state.fail = true;
			state.errorDetailData = action.payload;
        },

        // Products a bundle can take its stock from: real products with variations.
        doGetBundleSourceOptionsSuccess: (state, action) => {
            const rows = action.payload?.data || [];
            state.bundleSourceOptions = rows
                .filter((p) => p?.has_variants === 1 && p?.is_bundle !== 1)
                .map((p) => ({ id: p.id, name: p.name, variant_count: Number(p.variant_count || 0) }));
        },
        doGetBundleSourceOptionsFail: (state) => {
            state.bundleSourceOptions = [];
        },

        // importSuccess means "products were actually created" — this endpoint
        // returns 200 for the validation-failure outcome too (imported: false),
        // so an "HTTP 200 came back" flag would be meaningless.
        doImportProductManagementSuccess: (state, action) => {
            state.importSuccess = !!action.payload?.imported;
            state.importFail = false;
            state.importResponseData = action.payload;
        },
        doImportProductManagementFail: (state, action) => {
            state.importSuccess = false;
            state.importFail = true;
            state.errorDetailData = action.payload;
        },

        doDownloadProductImportTemplateSuccess: (state) => {
            state.downloadTemplateSuccess = true;
            state.downloadTemplateFail = false;
        },
        doDownloadProductImportTemplateFail: (state, action) => {
            state.downloadTemplateSuccess = false;
            state.downloadTemplateFail = true;
            state.errorDetailData = action.payload;
        },

        resetProductManagementStateSuccess: (state, action) => {
            state.success = action.payload;
            state.fail = action.payload;
			state.errorDetailData = null;
            state.assignSuccess = action.payload;
            state.assignFail = action.payload;
            state.assignReponseData = null;
            state.downloadQRSuccess = action.payload;
            state.downloadQRFail = action.payload;
            state.importSuccess = false;
            state.importFail = false;
            state.importResponseData = null;
            state.downloadTemplateSuccess = false;
            state.downloadTemplateFail = false;
        },

        resetProductManagementSuccess: (state, action) => {
            state.detailData = null;
        },
    },
});

export const doGetProductManagementList = (param) => async (dispatch) => {
    try {
        let url = `/api/admin/product-management?page=${param.page}&size=${param.limit}`;
        if(param.filter) {
            url += `&filter=${encodeURIComponent(param.filter)}`;
        }

        const response = await axios.get(url);
        dispatch(doGetProductManagementListSuccess(response.data));
    } catch (error) {
        dispatch(doGetProductManagementListFail(error.response));
    }
};

// Loaded for the bundle "stock comes from" picker. Kept separate from the list
// state so opening the form never disturbs the product list page.
export const doGetBundleSourceOptions = () => async (dispatch) => {
    try {
        const response = await axios.get(`/api/admin/product-management?page=1&size=500`);
        dispatch(doGetBundleSourceOptionsSuccess(response.data));
    } catch (error) {
        dispatch(doGetBundleSourceOptionsFail(error.response));
    }
};

export const doGetProductManagementSummary = () => async (dispatch) => {
    try {
        const url = `/api/admin/product-management/summary`;

        const response = await axios.get(url);
        dispatch(doGetProductManagementSummarySuccess(response.data));
    } catch (error) {
        dispatch(doGetProductManagementSummaryFail(error.response));
    }
};

export const doGetProductManagementDetail = (param) => async (dispatch) => {
    try {
        let url = `/api/admin/product-management/${param.product_id}`;
       
        const response = await axios.get(url);
        dispatch(doGetProductManagementDetailSuccess(response.data));
    } catch (error) {
        dispatch(doGetProductManagementDetailFail(error.response));
    }
};

export const doAddProductManagement = (param) => async (dispatch) => {
    try {
        const url = `/api/admin/product-management`;

        // Create FormData
        const formData = new FormData();

        // Add basic fields (name, sku, credit, stock_quantity, status)
        formData.append("name", param.name);
        formData.append("sku", param.sku || "");
        formData.append("brand", param.brand || "");
        formData.append("description", param.description);
        formData.append("credit", param.credit);
        formData.append("stock_quantity", param.stock_quantity);
        formData.append("product_category_id", param.product_category_id);
        formData.append("status", param.status || "active");

        // Variations (serialized as JSON strings — same multipart pattern as deleted_image_ids).
        // NumericFormat in the variant editor delivers credit/stock as strings, so we coerce
        // to numbers here — the backend's strict validator expects real numbers.
        formData.append("has_variants", param.has_variants ? "1" : "0");
        // A bundle that draws its stock from another product has no variations of
        // its own — the ones on screen belong to the source product and are shown
        // for reference. Posting them back would try to claim that product's SKUs.
        const sharesStock = Boolean(param.is_bundle && param.bundle_source_product_id);
        if (param.has_variants && !sharesStock) {
            // parseFloat (not Number) so empty strings/null become NaN → JSON.stringify
            // serializes them as null, which the backend's parseFloat then rejects.
            // Number("") is 0, which would silently pass validation.
            const normalizedVariants = (param.variants || []).map((v) => ({
                ...v,
                credit: parseFloat(v.credit),
                stock_quantity: parseFloat(v.stock_quantity),
            }));
            formData.append("options", JSON.stringify(param.options || []));
            formData.append("variants", JSON.stringify(normalizedVariants));
        }

        // Bundle config (fixed-price "pick N variants" product).
        formData.append("is_bundle", param.is_bundle ? "1" : "0");
        if (param.is_bundle) {
            formData.append("bundle_size", param.bundle_size);
            formData.append("bundle_price", param.bundle_price);
            // Which product's stock this bundle sells (blank = keeps its own).
            if (param.bundle_source_product_id) {
                formData.append("bundle_source_product_id", param.bundle_source_product_id);
            }
        }

        // Handle product_images object
        const productImages = param.product_images;

        if (productImages && typeof productImages === "object") {
            // Handle new image files
            if (productImages.images && Array.isArray(productImages.images)) {
                productImages.images.forEach((image) => {
                    if (image instanceof File) {
                        formData.append("images", image);
                    } else if (image.id) {
                        // Existing image - send ID
                        formData.append("existing_image_ids[]", image.id);
                    }
                });
            }

            // Handle deleted image IDs
            if (productImages.deleted_image_ids && productImages.deleted_image_ids.length > 0) {
                formData.append("deleted_image_ids", JSON.stringify(productImages.deleted_image_ids));
            }

            // Handle primary/cover image index
            if (typeof productImages.selected_image_index === "number") {
                formData.append("cover_image_index", productImages.selected_image_index);
            }
        }

        // formData.append('quotation_data', JSON.stringify(quotationData));
        const config = {
            headers: { 'content-type': 'multipart/form-data' }
        }

        const response = await axios.post(url, formData, config);
        dispatch(doAddProductManagementSuccess(response.data));
    } catch (error) {
        dispatch(doAddProductManagementFail(error.response));
    }
};

export const doUpdateProductManagement = (param, product_management_id) => async (dispatch) => {
    try {
        const url = `/api/admin/product-management/${product_management_id}`;

        // Create FormData
        const formData = new FormData();

        // Add basic fields (name, sku, credit, stock_quantity, status)
        formData.append("name", param.name);
        formData.append("sku", param.sku || "");
        formData.append("brand", param.brand || "");
        formData.append("description", param.description);
        formData.append("credit", param.credit);
        formData.append("stock_quantity", param.stock_quantity);
        formData.append("product_category_id", param.product_category_id);
        formData.append("status", param.status || "active");

        // Variations (serialized as JSON strings — same multipart pattern as deleted_image_ids).
        // NumericFormat in the variant editor delivers credit/stock as strings, so we coerce
        // to numbers here — the backend's strict validator expects real numbers.
        formData.append("has_variants", param.has_variants ? "1" : "0");
        // A bundle that draws its stock from another product has no variations of
        // its own — the ones on screen belong to the source product and are shown
        // for reference. Posting them back would try to claim that product's SKUs.
        const sharesStock = Boolean(param.is_bundle && param.bundle_source_product_id);
        if (param.has_variants && !sharesStock) {
            // parseFloat (not Number) so empty strings/null become NaN → JSON.stringify
            // serializes them as null, which the backend's parseFloat then rejects.
            // Number("") is 0, which would silently pass validation.
            const normalizedVariants = (param.variants || []).map((v) => ({
                ...v,
                credit: parseFloat(v.credit),
                stock_quantity: parseFloat(v.stock_quantity),
            }));
            formData.append("options", JSON.stringify(param.options || []));
            formData.append("variants", JSON.stringify(normalizedVariants));
        }

        // Bundle config (fixed-price "pick N variants" product).
        formData.append("is_bundle", param.is_bundle ? "1" : "0");
        if (param.is_bundle) {
            formData.append("bundle_size", param.bundle_size);
            formData.append("bundle_price", param.bundle_price);
            // Which product's stock this bundle sells (blank = keeps its own).
            if (param.bundle_source_product_id) {
                formData.append("bundle_source_product_id", param.bundle_source_product_id);
            }
        }

        // Handle product_images object
        const productImages = param.product_images;

        if (productImages && typeof productImages === "object") {
            // Handle new image files
            if (productImages.images && Array.isArray(productImages.images)) {
                productImages.images.forEach((image) => {
                    if (image instanceof File) {
                        formData.append("images", image);
                    } else if (image.id) {
                        // Existing image - send ID
                        formData.append("existing_image_ids[]", image.id);
                    }
                });
            }

            // Handle deleted image IDs
            if (productImages.deleted_image_ids && productImages.deleted_image_ids.length > 0) {
                formData.append("deleted_image_ids", JSON.stringify(productImages.deleted_image_ids));
            }

            // Handle primary/cover image index
            if (typeof productImages.selected_image_index === "number") {
                formData.append("cover_image_index", productImages.selected_image_index);
            }
        }

        const config = {
            headers: { 'content-type': 'multipart/form-data' }
        };

        const response = await axios.put(url, formData, config);
        dispatch(doUpdateProductManagementSuccess(response.data));
    } catch (error) {
        dispatch(doUpdateProductManagementFail(error.response));
    }
};

// Bulk import: one .xlsx + its image files in a single multipart request.
// opts: { signal, onUploadProgress } — same contract as doDownloadQRCodeCard.
// Returns the response body and re-throws on error (skipping the Fail dispatch
// for a user-initiated cancel) so the dialog can drive itself with await.
export const doImportProductManagement = (param, opts = {}) => async (dispatch) => {
    try {
        const url = `/api/admin/product-management/import`;

        const formData = new FormData();
        formData.append("file", param.file);
        (param.images || []).forEach((image) => formData.append("images", image));

        const config = {
            headers: { 'content-type': 'multipart/form-data' },
            signal: opts.signal,
            onUploadProgress: opts.onUploadProgress
                ? (e) => opts.onUploadProgress({ loaded: e.loaded || 0, total: e.total || 0 })
                : undefined,
        };

        const response = await axios.post(url, formData, config);
        dispatch(doImportProductManagementSuccess(response.data));
        return response.data;
    } catch (error) {
        // A user-initiated cancel is not a failure — only report real errors.
        if (!axios.isCancel(error)) {
            dispatch(doImportProductManagementFail(error.response));
        }
        throw error; // let the dialog distinguish cancel vs error and update the UI
    }
};

export const doDownloadProductImportTemplate = () => async (dispatch) => {
    try {
        const url = `/api/admin/product-management/import/template`;
        const response = await axios.get(url, { responseType: 'blob' });

        // Trigger download immediately
        const url2 = window.URL.createObjectURL(response?.data);
        const link = document.createElement('a');
        link.href = url2;
        link.download = "Product_Import_Template.xlsx";
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url2);

        dispatch(doDownloadProductImportTemplateSuccess());
    } catch (error) {
        dispatch(doDownloadProductImportTemplateFail(error.response));
        throw error; // dialog shows an inline error strip
    }
};

export const resetProductManagementState = () => async (dispatch) => {
    dispatch(resetProductManagementStateSuccess(false));
};

export const resetProductManagement = () => async (dispatch) => {
    dispatch(resetProductManagementSuccess(false));
};

export const { 
    doGetProductManagementListSuccess,
    doGetProductManagementListFail,

    doGetProductManagementSummarySuccess,
    doGetProductManagementSummaryFail,

    doGetProductManagementDetailSuccess,
    doGetProductManagementDetailFail,

    doAddProductManagementSuccess,
    doAddProductManagementFail,

    doUpdateProductManagementSuccess,
    doUpdateProductManagementFail,

    doGetBundleSourceOptionsSuccess,
    doGetBundleSourceOptionsFail,

    doImportProductManagementSuccess,
    doImportProductManagementFail,

    doDownloadProductImportTemplateSuccess,
    doDownloadProductImportTemplateFail,

    resetProductManagementStateSuccess,
    resetProductManagementSuccess,
} = productManagementSlide.actions;

export const getProductManagementList = (state) => state.productManagement?.listData;
export const getProductManagementListPage = (state) => state.productManagement?.page;
export const getProductManagementListPageCount = (state) => state.productManagement?.pageCount;
export const getProductManagementListTotal = (state) => state.productManagement?.totalRow;
export const getProductManagementSummary = (state) => state.productManagement?.summaryData;

export const getProductManagementDetail = (state) => state.productManagement?.detailData;
export const getProductManagementErrorDetailData = (state) => state.productManagement?.errorDetailData;

export const getAddProductManagementSuccess = (state) => state.productManagement?.success;
export const getAddProductManagementFail = (state) => state.productManagement?.fail;
export const getAddProductManagementResponseData = (state) => state.productManagement?.responseData;

export const getBundleSourceOptions = (state) => state.productManagement?.bundleSourceOptions;

export const getImportProductManagementSuccess = (state) => state.productManagement?.importSuccess;
export const getImportProductManagementFail = (state) => state.productManagement?.importFail;
export const getImportProductManagementResponseData = (state) => state.productManagement?.importResponseData;
export const getDownloadProductImportTemplateSuccess = (state) => state.productManagement?.downloadTemplateSuccess;
export const getDownloadProductImportTemplateFail = (state) => state.productManagement?.downloadTemplateFail;

export default productManagementSlide.reducer;
